import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { FormTemplateSummary } from '@isg/shared-types';
import {
  useFormKayitlar,
  useFormSablonCogalt,
  useFormSablonDurum,
  useFormSablonExcelIndir,
  useFormSablonKapsamSecenekleri,
  useFormSablonSil,
  useFormSablonlar,
  useWorkplacesTumu,
  useKapilar,
} from '../api/queries';
import type { FormKayitSuzgeci, FormSablonSuzgeci } from '../api/queries';
import { KapsamSecici } from './form-parts';
import { SatirEylemleri, KapiSebebi } from '../sozlesme';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * FORMLAR ekranı (F2·S4 — Kapsam §22.5): şablon listesi + dolu kayıt arşivi.
 *
 * ✏️ **TUR 3 (F7 — madde 143 + 144): DOLDURMA · ŞABLON · KAYIT ARTIK AYRI
 * SAYFA.** Üçü de bir MODALDA yaşıyordu; sayfa sözleşmesinin P1 kuralı
 * (*bağımsız kayıt ayrı sayfa, alt kalem satır içi*) bunu zaten söylüyordu
 * ama ekranlar sözleşmeden ÖNCE yazıldığı için modalda kalmışlardı. Şablon
 * ekranı ayrıca **yatayda genişledi** (144): hedef, on beş maddelik bir
 * checklist'i art arda giriş ritmiyle doldurabilmek.
 *
 * Görünürlük role göre SADELEŞİR, karar sunucudadır (§2.16): satır
 * düğmelerinin bayrakları `yetkiler` alanından gelir.
 */

const SAYFA_BOYUTU = 25;

/** Sabit seçenek dizisi — modül düzeyinde, render başına yeniden kurulmaz. */
const DURUM_SECENEKLERI = [
  { value: 'hepsi', label: 'Tümü' },
  { value: 'draft', label: 'Taslak' },
  { value: 'submitted', label: 'Gönderildi' },
];

/** Y1 — şablon durumu süzgeci. Varsayılan `active`: gizlenmiş şablon günlük
 *  işin görüntüsünü kirletmez, ama BİR TIKLA görünür (T13 — silme değil). */
const SABLON_DURUM_SECENEKLERI = [
  { value: 'active', label: 'Etkin' },
  { value: 'archived', label: 'Gizli' },
  { value: 'hepsi', label: 'Tümü' },
];

/**
 * ✏️ D2-1 (17.09.2026 — bulgu 92): FORMLAR İKİ SEKMEDİR — *Şablonlar* ·
 * *Dolu kayıtlar*. Şablon tasarlamak ile form doldurmak farklı işler,
 * farklı sıklıkta ve farklı kişilerce yapılır; aynı listede duruyorlardı.
 * Sekmeler rota tabanlıdır (`/formlar` · `/formlar/kayitlar` — ekipmanın
 * Envanter · Periyodik görevler · Görev kuralları deseni); menü satırı artmaz.
 */
export function FormKayitlariScreen(): React.JSX.Element {
  return (
    <Stack>
      <Title order={3}>Formlar</Title>
      <KayitListesi />
    </Stack>
  );
}

export function FormlarScreen(): React.JSX.Element {
  const navigate = useNavigate();
  // P13-b (madde 225): iki kapı SUNUCUDAN (`GET kapilar`) — doldurma `FORM_DOLDURMA` ·
  // yeni şablon `FORM_SABLON_YAZMA` (uçların kendi sabiti). ⚠️ Eski ekran *"Yeni şablon"*u
  // yalnız lokasyon yön. + uzmana çiziyordu; uç holding/şirket yön. de kabul eder —
  // görünürlük artık kapıyla aynı (bahçe/kapsam yüklemi şablonun kendi ucunda).
  const kapilar = useKapilar();
  const doldurabilir = kapilar.data?.formDoldurmaKapisi.yazabilir ?? false;
  const bahceliAcabilir = kapilar.data?.sablonKapisi.yazabilir ?? false;

  const [cogaltilanSablon, setCogaltilanSablon] = useState<FormTemplateSummary | null>(null);

  return (
    <Stack>
      <Title order={3}>Formlar</Title>
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.sablonKapisi}
          nesne="Şablonu"
          fiil="açar"
          testId="form-sablon-yazamama-sebebi"
        />
      )}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.formDoldurmaKapisi}
          nesne="Formu"
          fiil="doldurur"
          testId="form-doldurma-yazamama-sebebi"
        />
      )}
      <SablonListesi
        bahceliAcabilir={bahceliAcabilir}
        doldurabilir={doldurabilir}
        onCogalt={setCogaltilanSablon}
      />

      {cogaltilanSablon && (
        <CogaltmaModali
          kaynak={cogaltilanSablon}
          onKapat={() => setCogaltilanSablon(null)}
          onAcildi={(id) => {
            setCogaltilanSablon(null);
            void navigate(`/formlar/sablonlar/${id}`);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * ŞABLON LİSTESİ — **Y1: SUNUCU SAYFALAMASI + SÜZGEÇ + TOPLAM** (F4·S6).
 *
 * ⚠️ **P4 AÇIĞIYDI:** aynı sayfanın dolu kayıt listesi F3'ten beri sayfalıydı,
 * şablon listesi tümünü indiriyordu. Kapsam modeli şablon sayısını artırır
 * (her müşteri kendi işyerine form açabilir — T6).
 *
 * ⚠️ **İŞYERİ SÜZGECİ KAPSAM EKSENİNDEDİR** (T5'in yüzeydeki karşılığı):
 * *"bu işyerinde hangi formlar geçerli"* — ekranın en çok sorduğu soru.
 * Süzgeç ve sayfa SUNUCUYA gider; ekran hiçbir şeyi yerelde süzmez (§2.16/B3).
 */
function SablonListesi({
  bahceliAcabilir,
  doldurabilir,
  onCogalt,
}: {
  bahceliAcabilir: boolean;
  doldurabilir: boolean;
  onCogalt: (sablon: FormTemplateSummary) => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [suzgec, setSuzgec] = useState<FormSablonSuzgeci>({
    durum: 'active',
    sayfa: 1,
    sayfaBoyutu: SAYFA_BOYUTU,
  });
  const sablonlar = useFormSablonlar(suzgec);
  const sablonSil = useFormSablonSil();
  // T13-a — GİZLEME/GERİ AÇMA TETİKLEYİCİSİ. Uç F2·S4'ten, kanca 05.08'den
  // beri yerindeydi ve **çağıranı yoktu** (h-3): şablonu emekliye ayırmanın
  // ekranda hiçbir yolu yoktu.
  const durumYaz = useFormSablonDurum();
  const isyerleri = useWorkplacesTumu().data ?? [];

  const isyeriSecenekleri = useMemo(
    () => isyerleri.map((w) => ({ value: w.id, label: w.ad })),
    [isyerleri.map((w) => w.id).join(',')],
  );
  const suzgeciDegistir = (degisiklik: Partial<FormSablonSuzgeci>): void =>
    setSuzgec((onceki) => ({ ...onceki, ...degisiklik, sayfa: 1 }));
  const excelIndir = useFormSablonExcelIndir();

  const toplam = sablonlar.data?.toplam ?? 0;
  const sayfa = sablonlar.data?.sayfa ?? 1;
  const sonSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYUTU));
  const liste = sablonlar.data?.satirlar ?? [];

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <Group gap="xs" align="baseline">
          <Text fw={500}>Şablonlar</Text>
          <Text size="sm" c="dimmed" data-testid="form-sablon-toplam">
            {toplam} şablon
          </Text>
        </Group>
        {bahceliAcabilir && (
          <Group gap="xs">
            {/* ✏️ P1 (Namet grubu, 01.11.2026): EXCEL'DEN ŞABLON — iki düğme.
                İndirme sabit şablonu verir; yükleme *Yeni şablon* sayfasının
                Excel kipine gider (ad dosya adından, kapsam orada sorulur —
                kapsamsız şablon ölü doğar, §22.5c) ve kaydedince düzenleyiciye
                düşer. Önizleme ekranı YOK. */}
            <Button
              size="xs"
              variant="default"
              data-testid="form-sablon-excel-indir"
              loading={excelIndir.isPending}
              onClick={() => excelIndir.mutate()}
            >
              Excel şablonunu indir
            </Button>
            <Button
              size="xs"
              variant="default"
              data-testid="form-sablon-excel-yukle"
              onClick={() => void navigate('/formlar/sablonlar/yeni', { state: { excel: true } })}
            >
              Excel'den yükle
            </Button>
            <Button
              size="xs"
              variant="default"
              data-testid="form-sablon-yeni"
              onClick={() => void navigate('/formlar/sablonlar/yeni')}
            >
              Yeni şablon
            </Button>
          </Group>
        )}
      </Group>
      {excelIndir.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="form-sablon-excel-indir-hata">
          {excelIndir.error.message}
        </Alert>
      )}

      <Group gap="xs" align="flex-end" data-testid="form-sablon-suzgec">
        <TextInput
          size="xs"
          w={220}
          label="Ara"
          placeholder="Şablon adı"
          value={suzgec.ara ?? ''}
          onChange={(event) =>
            suzgeciDegistir({ ara: event.currentTarget.value.trim() || undefined })
          }
          data-testid="form-sablon-ara"
        />
        <Select
          size="xs"
          w={220}
          label="İşyeri"
          placeholder="Tümü"
          clearable
          data={isyeriSecenekleri}
          value={suzgec.workplaceId ?? null}
          onChange={(value) => suzgeciDegistir({ workplaceId: value })}
          data-testid="form-sablon-suzgec-isyeri"
        />
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Durum
          </Text>
          <SegmentedControl
            size="xs"
            data={SABLON_DURUM_SECENEKLERI}
            value={suzgec.durum}
            onChange={(value) => suzgeciDegistir({ durum: value as FormSablonSuzgeci['durum'] })}
            data-testid="form-sablon-suzgec-durum"
          />
        </Stack>
      </Group>

      <QueryStatus queries={[sablonlar]} />
      {sablonlar.isSuccess &&
        (liste.length === 0 ? (
          <BosListeNotu mesaj="Bu süzgeçle şablon yok." />
        ) : (
          <Table withTableBorder data-testid="form-sablon-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Şablon</Table.Th>
                {/* KAPSAM SÜTUNU (T2): bir şablonun düzeyi yoktur, KAPSAMI
                    vardır — sütun artık bir işyeri kümesini anlatır. */}
                <Table.Th w={200}>Kapsam</Table.Th>
                <Table.Th w={110}>Sürüm</Table.Th>
                <Table.Th w={90}>Kayıt</Table.Th>
                <Table.Th w={200} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {liste.map((sablon) => (
                <Table.Tr key={sablon.id} data-testid={`form-sablon-${sablon.kod}`}>
                  <Table.Td>
                    <Group gap={6}>
                      <Text size="sm">{sablon.ad}</Text>
                      {sablon.durum === 'archived' && (
                        <Badge
                          size="xs"
                          color={ANLAM.bilgi}
                          data-testid={`form-sablon-gizli-${sablon.kod}`}
                        >
                          gizli
                        </Badge>
                      )}
                      {sablon.bildirimUretir && (
                        <Badge size="xs" color={ANLAM.bilgi} variant="light">
                          bildirim üretir
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td data-testid={`form-sablon-kapsam-${sablon.kod}`}>
                    <Text size="sm">{kapsamOzeti(sablon)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Text size="sm">v{sablon.yayinliSurumNo ?? '—'}</Text>
                      {sablon.taslakSurumNo !== null && (
                        <Badge size="xs" color={ANLAM.dikkat}>
                          taslak v{sablon.taslakSurumNo}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{sablon.kayitSayisi}</Text>
                  </Table.Td>
                  <Table.Td>
                    {/* P2/P2-a: satırda BİR birincil eylem (Doldur); yönetim
                        eylemleri ⋮ altında, Sil TEHLİKELİ ve kütüphane
                        nüshasında HİÇ yok (sunucu bayrağı `yetkiler.silebilir`).
                        S8-4: İZİN TÜRÜ şablonunda Doldur YOK. */}
                    <Group justify="flex-end">
                      <SatirEylemleri
                        testId={`form-sablon-eylemler-${sablon.kod}`}
                        birincil={
                          doldurabilir && sablon.durum === 'active' && !sablon.izinTuruFormu
                            ? {
                                etiket: 'Doldur',
                                testId: `form-doldur-${sablon.kod}`,
                                onClick: () => void navigate(`/formlar/doldur/${sablon.id}`),
                              }
                            : undefined
                        }
                        digerleri={[
                          {
                            etiket: 'Şablonu düzenle',
                            testId: `form-sablon-duzenle-${sablon.kod}`,
                            gizli: !sablon.yetkiler.duzenleyebilir,
                            onClick: () => void navigate(`/formlar/sablonlar/${sablon.id}`),
                          },
                          {
                            etiket: 'Çoğalt',
                            testId: `form-sablon-cogalt-${sablon.kod}`,
                            gizli: !sablon.yetkiler.cogaltabilir,
                            onClick: () => onCogalt(sablon),
                          },
                          // T13 — EMEKLİYE AYIRMA = GİZLEME (silme değil):
                          // mevcut kayıtlar okunur, yeni doldurma açılmaz ve
                          // o şablondan görev üreten tanımlar ASKIYA DÜŞER
                          // (T15 — kendiliğinden geri gelir).
                          {
                            etiket: sablon.durum === 'active' ? 'Gizle' : 'Geri göster',
                            testId: `form-sablon-durum-${sablon.kod}`,
                            gizli: !sablon.yetkiler.duzenleyebilir,
                            onClick: () =>
                              durumYaz.mutate({
                                templateId: sablon.id,
                                durum: sablon.durum === 'active' ? 'archived' : 'active',
                              }),
                          },
                          {
                            etiket: 'Sil',
                            testId: `form-sablon-sil-${sablon.kod}`,
                            gizli: !sablon.yetkiler.silebilir,
                            tehlikeli: true,
                            onClick: () => sablonSil.mutate({ templateId: sablon.id }),
                          },
                        ]}
                      />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ))}

      <Group justify="flex-end" gap="xs">
        <Button
          size="xs"
          variant="default"
          disabled={sayfa <= 1}
          data-testid="form-sablon-onceki"
          onClick={() => setSuzgec((o) => ({ ...o, sayfa: o.sayfa - 1 }))}
        >
          Önceki
        </Button>
        <Text size="xs" c="dimmed" data-testid="form-sablon-sayfa">
          {sayfa} / {sonSayfa}
        </Text>
        <Button
          size="xs"
          variant="default"
          disabled={sayfa >= sonSayfa}
          data-testid="form-sablon-sonraki"
          onClick={() => setSuzgec((o) => ({ ...o, sayfa: o.sayfa + 1 }))}
        >
          Sonraki
        </Button>
      </Group>

      {(sablonSil.isError || durumYaz.isError) && (
        <Alert color={ANLAM.engel} role="alert" data-testid="form-sablon-sil-hata">
          {(sablonSil.error ?? durumYaz.error)?.message}
        </Alert>
      )}
    </Stack>
  );
}

/**
 * KAPSAM ÖZETİ (Y5) — *"Holding"* / *"matbu"* KALKTI (T1): kullanıcı bir
 * DÜZEY değil bir İŞYERİ KÜMESİ okur. Ekran kural türetmez; sunucunun
 * verdiği üç alanı okur.
 */
function kapsamOzeti(sablon: FormTemplateSummary): string {
  if (sablon.kapsamIsyeriSayisi === 0) return 'Kapsam yok';
  if (sablon.kapsamTumIsyerleri) return `Tüm işyerleri (${sablon.kapsamIsyeriSayisi})`;
  if (sablon.kapsamIsyeriSayisi <= sablon.kapsamAdlari.length) {
    return sablon.kapsamAdlari.join(', ');
  }
  return `${sablon.kapsamAdlari.join(', ')} +${sablon.kapsamIsyeriSayisi - sablon.kapsamAdlari.length}`;
}

/**
 * F3 — DOLU KAYIT ARŞİVİ: **görünür süzgeç + sayfalama** (P4).
 *
 * ⚠️ Süzgeç ve sayfa SUNUCUYA gider; ekran hiçbir şeyi yerelde süzmez.
 * Toplam sayı her zaman yazılır — kullanıcı *"neyi elinde tuttuğunu"* bilsin.
 */
function KayitListesi(): React.JSX.Element {
  // Süzgeç havuzu: TÜM şablonlar (sayfasız) — süzgeç listesi sayfalanmaz.
  const sablonlar =
    useFormSablonlar({ durum: 'hepsi', sayfa: 1, sayfaBoyutu: 100 }).data?.satirlar ?? [];
  const [suzgec, setSuzgec] = useState<FormKayitSuzgeci>({
    durum: 'hepsi',
    sayfa: 1,
    sayfaBoyutu: SAYFA_BOYUTU,
  });
  // ✏️ TUR 9 / B4 (T9-18): şirket başına fan-out yerine TEK istek.
  const isyerleri = useWorkplacesTumu().data ?? [];
  const kayitlar = useFormKayitlar(suzgec);

  // ⚠️ **SEÇENEK DİZİLERİ SABİTLENİR.** `useQueries` her render'da YENİ bir
  // dizi döner; ondan türetilen `data` da her render'da yeni bir kimlik alır
  // ve Mantine'ın açılır listesi kendini KAPATIR (e2e'de yakalandı: seçenek
  // DOM'da duruyor ama görünmüyor). Bağımlılık kimlik listesidir.
  const sablonSecenekleri = useMemo(
    () => sablonlar.map((s) => ({ value: s.id, label: s.ad })),
    [sablonlar.map((s) => s.id).join(',')],
  );
  const isyeriSecenekleri = useMemo(
    () => isyerleri.map((w) => ({ value: w.id, label: w.ad })),
    [isyerleri.map((w) => w.id).join(',')],
  );

  const suzgeciDegistir = (degisiklik: Partial<FormKayitSuzgeci>): void =>
    // Süzgeç değişince SAYFA 1'e döner: aksi hâlde daralan sonuçta boş bir
    // 4. sayfada kalınır ve kullanıcı "kayıt yok" sanır.
    setSuzgec((onceki) => ({ ...onceki, ...degisiklik, sayfa: 1 }));

  const toplam = kayitlar.data?.toplam ?? 0;
  const sayfa = kayitlar.data?.sayfa ?? 1;
  const sonSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYUTU));

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <Text fw={500}>Dolu kayıtlar</Text>
        <Text size="sm" c="dimmed" data-testid="form-kayit-toplam">
          {toplam} kayıt
        </Text>
      </Group>

      <Group gap="xs" align="flex-end" data-testid="form-kayit-suzgec">
        <Select
          size="xs"
          w={220}
          label="Şablon"
          placeholder="Tümü"
          clearable
          data={sablonSecenekleri}
          value={suzgec.formTemplateId ?? null}
          onChange={(value) => suzgeciDegistir({ formTemplateId: value })}
          data-testid="form-suzgec-sablon"
        />
        <Select
          size="xs"
          w={200}
          label="İşyeri"
          placeholder="Tümü"
          clearable
          data={isyeriSecenekleri}
          value={suzgec.workplaceId ?? null}
          onChange={(value) => suzgeciDegistir({ workplaceId: value })}
          data-testid="form-suzgec-isyeri"
        />
        {/* ⚠️ DURUM SÜZGECİ AÇILIR LİSTE DEĞİL: üç sabit değer her zaman
            GÖRÜNÜR durur (P4 — "görünür süzgeç"), tek dokunuşla değişir ve
            hangi süzgecin açık olduğu bakışta okunur. */}
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Durum
          </Text>
          <SegmentedControl
            size="xs"
            data={DURUM_SECENEKLERI}
            value={suzgec.durum ?? 'hepsi'}
            onChange={(value) =>
              suzgeciDegistir({ durum: (value as FormKayitSuzgeci['durum']) ?? 'hepsi' })
            }
            data-testid="form-suzgec-durum"
          />
        </Stack>
        <TextInput
          size="xs"
          type="date"
          label="Başlangıç"
          value={suzgec.baslangic ?? ''}
          onChange={(event) => suzgeciDegistir({ baslangic: event.currentTarget.value || null })}
          data-testid="form-suzgec-baslangic"
        />
        <TextInput
          size="xs"
          type="date"
          label="Bitiş"
          value={suzgec.bitis ?? ''}
          onChange={(event) => suzgeciDegistir({ bitis: event.currentTarget.value || null })}
          data-testid="form-suzgec-bitis"
        />
      </Group>

      <QueryStatus queries={[kayitlar]} />
      {kayitlar.isSuccess &&
        (kayitlar.data.satirlar.length === 0 ? (
          <BosListeNotu mesaj="Bu süzgeçle form kaydı yok." />
        ) : (
          <>
            <Table withTableBorder data-testid="form-kayit-tablosu">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Form</Table.Th>
                  <Table.Th>İşyeri</Table.Th>
                  <Table.Th>İş ekipmanı</Table.Th>
                  <Table.Th w={120}>Tarih</Table.Th>
                  <Table.Th w={110}>Durum</Table.Th>
                  <Table.Th>Dolduran</Table.Th>
                  <Table.Th w={140} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {kayitlar.data.satirlar.map((kayit) => (
                  <Table.Tr key={kayit.id} data-testid={`form-kayit-${kayit.id}`}>
                    <Table.Td>
                      <Text size="sm">
                        {kayit.formTemplateAd}{' '}
                        <Text span size="xs" c="dimmed">
                          v{kayit.surumNo}
                        </Text>
                      </Text>
                    </Table.Td>
                    <Table.Td>{kayit.workplaceAd}</Table.Td>
                    <Table.Td>{kayit.ekipmanAd ?? '—'}</Table.Td>
                    <Table.Td>{tarihGoster(kayit.doldurmaTarihi)}</Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={kayit.durum === 'submitted' ? ANLAM.olumlu : ANLAM.dikkat}
                        variant="light"
                      >
                        {kayit.durum === 'submitted' ? 'Gönderildi' : 'Taslak'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{kayit.dolduranEtiket}</Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {/* S8-4: dolu izin formu arşivde İZİN BAĞLANTISIYLA
                            görünür — akış izin kartında sürer. */}
                        {kayit.ptwPermitId && (
                          <Button
                            size="xs"
                            variant="subtle"
                            component={Link}
                            to={`/izinler/${kayit.ptwPermitId}`}
                            data-testid={`form-kayit-izin-${kayit.id}`}
                          >
                            İzin
                          </Button>
                        )}
                        <Button
                          size="xs"
                          variant="subtle"
                          component={Link}
                          to={`/formlar/kayitlar/${kayit.id}`}
                          data-testid={`form-kayit-ac-${kayit.id}`}
                        >
                          Aç
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="flex-end" gap="xs" data-testid="form-kayit-sayfalama">
              <Button
                size="xs"
                variant="default"
                disabled={sayfa <= 1}
                data-testid="form-kayit-onceki"
                onClick={() => setSuzgec((o) => ({ ...o, sayfa: Math.max(1, (o.sayfa ?? 1) - 1) }))}
              >
                Önceki
              </Button>
              <Text size="sm" c="dimmed">
                {sayfa} / {sonSayfa}
              </Text>
              <Button
                size="xs"
                variant="default"
                disabled={sayfa >= sonSayfa}
                data-testid="form-kayit-sonraki"
                onClick={() => setSuzgec((o) => ({ ...o, sayfa: (o.sayfa ?? 1) + 1 }))}
              >
                Sonraki
              </Button>
            </Group>
          </>
        ))}
    </Stack>
  );
}

/**
 * ÇOĞALTMA — Ş5: **AD ZORUNLUDUR**, sistem otomatik "(kopya)" ÜRETMEZ.
 *
 * ⚠️ **143'ün istediği sadeleşme burada da uygulandı:** kapsam ve aksiyon
 * kaynağı artık ŞABLON SAYFASININ künyesindedir; bu pencere yalnızca
 * kaçınılmaz olan tek soruyu sorar (ad) ve doğrudan şablon sayfasına götürür.
 */
function CogaltmaModali({
  kaynak,
  onKapat,
  onAcildi,
}: {
  kaynak: FormTemplateSummary;
  onKapat: () => void;
  onAcildi: (templateId: string) => void;
}): React.JSX.Element {
  const cogalt = useFormSablonCogalt();
  const havuz = useFormSablonKapsamSecenekleri(kaynak.id);
  const [ad, setAd] = useState('');
  // ⚠️ Y2 — ÇOĞALTMADA DA KAPSAM SORULUR. Bugüne kadar hiç sorulmuyordu:
  // kopya sessizce açanın bahçesine bağlanıyordu ve kullanıcı kopyanın
  // hangi işyerlerinde geçerli olduğunu HİÇBİR YERDE görmüyordu.
  const [kapsam, setKapsam] = useState<string[]>([]);

  return (
    <Modal opened onClose={onKapat} title={`Şablonu çoğalt — ${kaynak.ad}`} size="md">
      <Stack>
        {/* ✏️ Y5/D2: *"merkezî"* kavramı KALKTI (T1) — açıklama CÜMLESİ de
            kaldırıldı, yerine yenisi YAZILMADI (M1: ekran cümlesi kuralı).
            Kopyanın bağımsız yaşadığını akışın kendisi söyler. */}
        <TextInput
          label="Şablon adı"
          required
          value={ad}
          onChange={(event) => setAd(event.currentTarget.value)}
          data-testid="bahceli-sablon-ad"
        />
        <KapsamSecici
          isyerleri={havuz.data?.isyerleri ?? []}
          secili={kapsam}
          onDegisti={setKapsam}
          testId="cogaltma-kapsam"
        />
        {cogalt.isError && (
          <Alert color={ANLAM.engel} role="alert" data-testid="bahceli-sablon-hata">
            {cogalt.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          {(ad.trim().length === 0 || kapsam.length === 0) && (
            <Text size="xs" c="dimmed" data-testid="cogaltma-engel">
              {ad.trim().length === 0 ? 'Şablon adı girin' : 'En az bir işyeri seçin'}
            </Text>
          )}
          <Button
            disabled={ad.trim().length === 0 || kapsam.length === 0 || cogalt.isPending}
            data-testid="bahceli-sablon-kaydet"
            onClick={() =>
              cogalt.mutate(
                { templateId: kaynak.id, ad: ad.trim(), kapsamIsyeriIdleri: kapsam },
                { onSuccess: (s: FormTemplateSummary) => onAcildi(s.id) },
              )
            }
          >
            Çoğalt
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
